import React, { useEffect, useState, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Character from '../components/Character';
import Header from '../components/Header';
import StarBackground from '../components/StarBackground';
import PlanetsBackground from '../components/PlanetsBackground';
import { getIntroConversation, getNextButtonText } from '../config/introConversations';
import { AppContext } from '../contexts/AppContext';
import { getAssetUrl } from '../api/config';
import MicTest from '../components/MicTest';
import '../styles/Intro.css';
import sentencesConfig from "../exercises/sentences/sentencesConfig";
import nonWordsConfig from "../exercises/nonWords/nonWordsConfig";
import syllablesConfig from "../exercises/syllables/syllablesConfig";

export default function Intro({exerciseId}) {
    
    const [step, setStep] = useState(0);
    const [conversation, setConversation] = useState([]);
    const [characters, setCharacters] = useState([]);
    const [planetImage, setPlanetImage] = useState(exerciseId && exerciseId !== 'hidingGame' ? '/assets/images/hiding/Planet_Background_blue.png' : null);
    const [headerColor, setHeaderColor] = useState('#ef3c3c');
    const [currentLanguage, setCurrentLanguage] = useState('it');
    const [nextButtonText, setNextButtonText] = useState('Avanti →');
    const [currentScene, setCurrentScene] = useState(null);
    const [displayedText, setDisplayedText] = useState('');
    const [currentSpeaker, setCurrentSpeaker] = useState('');
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    
    const { enableText, enableAudio, setEnableText, setEnableAudio } = useContext(AppContext);
    const audioRef = useRef(null);
    const subAudioRef = useRef(null); // Ref for audio triggered by timestamps
    const videoRef = useRef(null);
    const lastTimestampIndexRef = useRef(-1); // To avoid re-triggering sub-audio
    const navigate = useNavigate();

    // Use a ref for handleNext to ensure event listeners always use the latest version
    const handleNextRef = useRef();
    
    const handleNext = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        if (subAudioRef.current) {
            subAudioRef.current.pause();
            subAudioRef.current.currentTime = 0;
        }
        
        if (step < conversation.length - 1) {
            setStep(prev => prev + 1);
        } else {
            const introType = localStorage.getItem('introType');
            const exerciseId = localStorage.getItem('exerciseId');
            if (introType === 'general' || !exerciseId) {
                navigate('/pages/exePage');
            } else {
                if (exerciseId === 'hidingGame') {
                    navigate(`/exercise/${exerciseId}/main`);
                } else {
                    navigate(`/exercise/${exerciseId}/tutorial`);
                }
            }
        }
    };

    useEffect(() => {
        handleNextRef.current = handleNext;
    });
    
    useEffect(() => {
        // Get current language from localStorage
        const savedLanguage = localStorage.getItem('selectedLanguage') || 'it';
        setCurrentLanguage(savedLanguage);
        
        const exerciseId = localStorage.getItem('exerciseId');
        
        if (!exerciseId) {
            // General intro case
            const selectedConversation = getIntroConversation(savedLanguage, 'general');
            setConversation(selectedConversation);
            
            setCharacters(['Mars']);
            
            setHeaderColor('#64798A');
            setPlanetImage(null);
            
            const buttonText = getNextButtonText(savedLanguage);
            setNextButtonText(buttonText);
        } else {
            // Exercise intro case
            const selectedConversation = getIntroConversation(savedLanguage, exerciseId);
            setConversation(selectedConversation);
            
            // Get next button text in the selected language
            const buttonText = getNextButtonText(savedLanguage);
            setNextButtonText(buttonText);
            
            // Extract unique character names from the conversation including timestamps
            const allNames = [];
            selectedConversation.forEach(msg => {
                if (msg.name) allNames.push(msg.name);
                if (msg.timestamps) {
                    msg.timestamps.forEach(t => {
                        if (t.name) allNames.push(t.name);
                    });
                }
            });
            const uniqueNames = [...new Set(allNames)];
            setCharacters(uniqueNames.slice(0, 2));
            
            // Set colors and planet based on exercise
            let color = '#64798A';
            let planet = '/assets/images/hiding/Planet_Background_blue.png';
            
            if (exerciseId === 'syllables') {
                color = syllablesConfig.headerColor;
                planet = syllablesConfig.planetBackground || '/assets/images/hiding/Planet_Background_blue.png';
            } else if (exerciseId === 'nonWords') {
                color = nonWordsConfig.headerColor;
                planet = nonWordsConfig.planetBackground || '/assets/images/hiding/Planet_Background_blue.png';
            } else if (exerciseId === 'sentences') {
                color = sentencesConfig.headerColor;
                planet = sentencesConfig.planetBackground;
            } else if (exerciseId === 'hidingGame') {
                color = '#64798A';
                planet = null;
            }
            
            setHeaderColor(color);
            setPlanetImage(planet);
        }
    }, []);
    
    // Listen for language changes
    useEffect(() => {
        const handleLanguageChange = () => {
            const newLanguage = localStorage.getItem('selectedLanguage') || 'it';
            if (newLanguage !== currentLanguage) {
                setCurrentLanguage(newLanguage);
                
                // Update conversation and button text
                const exerciseId = localStorage.getItem('exerciseId');
                if (!exerciseId) {
                    const newConversation = getIntroConversation(newLanguage, 'general');
                    setConversation(newConversation);
                    
                    setCharacters(['Mars']);
                    
                    setPlanetImage(null);
                    
                    const newButtonText = getNextButtonText(newLanguage);
                    setNextButtonText(newButtonText);
                } else {
                    const newConversation = getIntroConversation(newLanguage, exerciseId);
                    setConversation(newConversation);
                    
                    const newButtonText = getNextButtonText(newLanguage);
                    setNextButtonText(newButtonText);
                    
                    const allNames = [];
                    newConversation.forEach(msg => {
                        if (msg.name) allNames.push(msg.name);
                        if (msg.timestamps) {
                            msg.timestamps.forEach(t => {
                                if (t.name) allNames.push(t.name);
                            });
                        }
                    });
                    const uniqueNames = [...new Set(allNames)];
                    setCharacters(uniqueNames.slice(0, 2));
                    
                    let color = '#64798A';
                    let planet = '/assets/images/hiding/Planet_Background_blue.png';
                    
                    if (exerciseId === 'syllables') {
                        color = syllablesConfig.headerColor;
                        planet = syllablesConfig.planetBackground || '/assets/images/hiding/Planet_Background_blue.png';
                    } else if (exerciseId === 'nonWords') {
                        color = nonWordsConfig.headerColor;
                        planet = nonWordsConfig.planetBackground || '/assets/images/hiding/Planet_Background_blue.png';
                    } else if (exerciseId === 'sentences') {
                        color = sentencesConfig.headerColor;
                        planet = sentencesConfig.planetBackground;
                    } else if (exerciseId === 'hidingGame') {
                        color = '#64798A';
                        planet = null;
                    }
                    
                    setHeaderColor(color);
                    setPlanetImage(planet);
                }
                
                // Reset step to show updated conversation from beginning
                setStep(0);
                
                // Stop any current audio
                if (audioRef.current) {
                    audioRef.current.pause();
                    audioRef.current.currentTime = 0;
                }
            }
        };
        
        // Check for language changes periodically
        const interval = setInterval(handleLanguageChange, 500);
        
        return () => clearInterval(interval);
    }, [currentLanguage]);
    
    // Update scene when step changes
    useEffect(() => {
        if (conversation.length === 0 || step >= conversation.length) return;
        
        const currentMessage = conversation[step];
        
        // Get scene from message
        if (currentMessage.scene) {
            setCurrentScene(currentMessage.scene);
        } else {
            setCurrentScene(null);
        }
    }, [step, conversation]);
    
    useEffect(() => {
        if (conversation.length === 0 || step >= conversation.length) return;
        
        const currentMessage = conversation[step];
        let audio = null;
        
        // Reset displayed text and speaker immediately
        setDisplayedText(currentMessage.text || '');
        setCurrentSpeaker(currentMessage.name || '');
        setIsVideoPlaying(false);
        lastTimestampIndexRef.current = -1; // Reset for new step
        
        // Handle Audio
        if (enableAudio && currentMessage.audio) {
            audio = new Audio(getAssetUrl(currentMessage.audio));
            audioRef.current = audio;
            
            audio.play().catch(error => {
                console.warn(`Could not play audio: ${error.message}`);
            });
            
            // Se autoNext non è esplicitamente false, procedi automaticamente alla fine dell'audio
            if (currentMessage.autoNext !== false) {
                audio.onended = () => handleNextRef.current();
            }
        }
        
        // Handle text sync and autoNext for video (if no audio)
        const setupSync = () => {
            const syncSource = audio || videoRef.current;
            if (syncSource) {
                if (currentMessage.timestamps) {
                    syncSource.ontimeupdate = () => {
                        const currentTime = syncSource.currentTime;
                        
                        // Find current timestamp
                        const eligible = currentMessage.timestamps
                            .map((t, index) => ({ ...t, index }))
                            .filter(t => t.time <= currentTime);
                        
                        const sub = eligible[eligible.length - 1];
                        
                        if (sub) {
                            setDisplayedText(sub.text);
                            if (sub.name) {
                                setCurrentSpeaker(sub.name);
                            }
                            
                            // Trigger sub-audio if present and not already played for this timestamp
                            if (enableAudio && sub.audio && lastTimestampIndexRef.current !== sub.index) {
                                lastTimestampIndexRef.current = sub.index;
                                
                                if (subAudioRef.current) {
                                    subAudioRef.current.pause();
                                }
                                
                                const sAudio = new Audio(getAssetUrl(sub.audio));
                                subAudioRef.current = sAudio;
                                sAudio.play().catch(e => console.warn("Sub-audio play error:", e));
                                
                                // If this is the last timestamp and has sub-audio, autoNext should wait for it
                                if (currentMessage.autoNext && sub.index === currentMessage.timestamps.length - 1) {
                                    sAudio.onended = () => handleNextRef.current();
                                }
                            }
                        }
                    };
                }
                
                // If no audio but autoNext on video (and no sub-audio waiting)
                if (!audio && currentMessage.autoNext && currentMessage.scene?.video) {
                    syncSource.onended = () => {
                        // Only autoNext if no sub-audio is currently playing or expected
                        if (!subAudioRef.current || subAudioRef.current.ended) {
                            handleNextRef.current();
                        }
                    };
                }
            } else {
                // Fallback for autoNext with duration if neither audio nor video is used for timing
                if (currentMessage.autoNext && currentMessage.duration) {
                    const timerId = setTimeout(() => handleNextRef.current(), currentMessage.duration);
                    return () => clearTimeout(timerId);
                }
            }
        };

        // We might need a small delay to ensure videoRef is populated if scene changed
        const timeoutId = setTimeout(setupSync, 50);

        return () => {
            clearTimeout(timeoutId);
            if (audio) {
                audio.pause();
                audio.onended = null;
                audio.ontimeupdate = null;
            }
            if (subAudioRef.current) {
                subAudioRef.current.pause();
                subAudioRef.current.onended = null;
            }
            if (videoRef.current) {
                videoRef.current.onended = null;
                videoRef.current.ontimeupdate = null;
            }
        };
    }, [step, conversation, enableAudio]);
    
    const handleNextBtnClick = () => {
        handleNext();
    };
    
    // Determine what to render based on scene data
    const getSceneBackground = () => {
        if (currentScene?.background) {
            return currentScene.background;
        }
        return planetImage;
    };
    
    const shouldShowPlanetsBackground = () => {
        if (currentScene?.usePlanetsBackground) {
            return true;
        }
        // If scene exists but has no background/video, use default behavior
        if (currentScene && !currentScene.background && !currentScene.video) {
            return !planetImage || localStorage.getItem('exerciseId') === 'hidingGame';
        }
        return (!planetImage || localStorage.getItem('exerciseId') === 'hidingGame') && !currentScene;
    };
    
    return (
        <div className="wrapper">
            <Header
                color={headerColor}
                enableText={enableText}
                setEnableText={setEnableText}
                enableAudio={enableAudio}
                setEnableAudio={setEnableAudio}
            />
            <StarBackground 
                imagePath={currentScene?.video ? "none" : planetImage} 
                hideStars={!!currentScene?.video} 
            />
            
            <div className="scene">
                {/* Video background if specified */}
                {currentScene?.video && (
                    <div className="videoWrapper">
                        <video
                            ref={videoRef}
                            key={currentScene.video}
                            className="framedVideo"
                            autoPlay
                            loop={currentScene.videoLoop !== false}
                            muted={currentScene.videoMuted !== false}
                            playsInline
                            onPlay={() => setIsVideoPlaying(true)}
                            onPause={() => setIsVideoPlaying(false)}
                            onEnded={() => setIsVideoPlaying(false)}
                        >
                            <source src={currentScene.video} type="video/mp4" />
                        </video>
                    </div>
                )}
                
                {/* Background planets (dynamic) if no specific planetImage and no video */}
                {!currentScene?.video && !planetImage && shouldShowPlanetsBackground() && (
                    <PlanetsBackground />
                )}
                
                {/* Custom HTML content if specified */}
                {currentScene?.html && (
                    <div
                        className="sceneHtmlContent"
                        dangerouslySetInnerHTML={{ __html: currentScene.html }}
                    />
                )}
                
                {/* Custom images if specified */}
                {currentScene?.images && currentScene.images.map((img, index) => (
                    <img
                        key={index}
                        src={img.src}
                        alt={img.alt || `Scene image ${index}`}
                        className={img.className || 'sceneImage'}
                        style={img.style || {}}
                    />
                ))}

                {/* MicTest Component if specified */}
                {currentScene?.component === 'MicTest' && (
                    <div className="sceneHtmlContent">
                        <MicTest onComplete={handleNext} />
                    </div>
                )}
                
                {/* First character on the bottom left */}
                {characters[0] && currentScene?.hideCharacters !== true && currentScene?.hideCharacters !== 'left' && (
                    <Character
                        name={characters[0]}
                        position="left"
                        isActive={characters.length === 1 || currentSpeaker === characters[0] || !currentSpeaker}
                    />
                )}
                
                {/* FUMETTO AGGIORNATO CON CLASSI DINAMICHE */}
                {enableText && displayedText && (
                    <div
                        className={`bubble ${currentSpeaker === characters[1] ? 'speaker-right' : 'speaker-left'}`}
                    >
                        {displayedText}
                    </div>
                )}
                
                {/* Second character on the bottom right (if exists) */}
                {characters[1] && currentScene?.hideCharacters !== true && currentScene?.hideCharacters !== 'right' && (
                    <Character
                        name={characters[1]}
                        position="right"
                        isActive={currentSpeaker === characters[1] || (!currentSpeaker && characters.length > 1)}
                    />
                )}
                
                {step < conversation.length && currentScene?.component !== 'MicTest' && !(currentScene?.video && isVideoPlaying) && (
                    <button className="nextButton" onClick={handleNextBtnClick}>
                        {nextButtonText}
                    </button>
                )}
            </div>
        </div>
    );
}
